/* ==========================================================================
   FLC Forge — Cart Drawer
   ========================================================================== */

const CART_SECTION = 'cart-line-items';

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer      = this.querySelector('[data-cart-drawer-inner]');
    this.overlay     = document.getElementById('overlay');
    this.closeBtn    = this.querySelector('[data-cart-close]');
    this.itemsWrap   = this.querySelector('[data-cart-items]');
    this.emptyState  = this.querySelector('[data-cart-empty]');
    this.footer      = this.querySelector('[data-cart-footer]');
    this.subtotal    = this.querySelectorAll('[data-cart-subtotal]');
    this.noteWrap    = this.querySelector('[data-cart-note-wrap]');
    this.noteInput   = this.noteWrap?.querySelector('textarea[name="note"]');
    this.progressBar = this.querySelector('[data-cart-progress]');
    this.thresholdWrap = this.querySelector('[data-cart-threshold]');
    this.discountThresholdWrap = this.querySelector('[data-cart-discount-threshold]');
    this.discountSummary = this.querySelector('[data-cart-discount-summary]');
    this.subtotalCompare = this.querySelectorAll('[data-cart-subtotal-compare]');
    // DEV-80: recommendation-based upsell shell (see snippets/cart-drawer.liquid)
    this.upsellWrap  = this.querySelector('[data-cart-upsell]');
    this.upsellKey   = null; // cache key (seed product + cart contents) to avoid redundant fetches
    this.noteSaveTimeout = null;
    this.noteSaveRequest = null;

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('flc:cart:open',       () => this.open());
    document.addEventListener('flc:drawers:close-all', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

    // iOS/Safari swipe-back restores the page from bfcache with the drawer still open.
    // Closing it here ensures the drawer is dismissed on back-navigation.
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.close(); });

    // flc:cart:updated may carry pre-rendered section HTML (from add-to-cart flows)
    document.addEventListener('flc:cart:updated', (e) => this.renderCart(e.detail));
    document.addEventListener('flc:cart:refresh',  () => this.refreshCart());

    this.itemsWrap?.addEventListener('change', (e) => {
      if (e.target.matches('[data-quantity-input]')) this.updateQuantity(e.target);
    });

    this.itemsWrap?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove-item]');
      if (btn) this.removeItem(btn);
    });

    this.noteInput?.addEventListener('input', () => this.scheduleNoteSave());

    this.querySelector('[data-checkout-btn]')?.addEventListener('click', async () => {
      await this.flushNoteSave();
      window.location.href = window.flcForge.routes.checkout_url;
    });
  }

  /* ---- Open / close ---- */

  open() {
    this.setAttribute('aria-hidden', 'false');
    this.drawer?.classList.add('is-open');
    FLC.openOverlay();
    FLC.trapFocus(this);
    this.refreshCart();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.drawer?.classList.remove('is-open');
    FLC.closeOverlay();
  }

  setLoading(state) {
    this.progressBar?.classList.toggle('is-loading', state);
  }

  scheduleNoteSave() {
    if (!this.noteInput) return;
    clearTimeout(this.noteSaveTimeout);
    this.noteSaveTimeout = setTimeout(() => {
      this.noteSaveRequest = this.saveNote(this.noteInput.value);
    }, 350);
  }

  async flushNoteSave() {
    if (!this.noteInput) return;
    clearTimeout(this.noteSaveTimeout);
    this.noteSaveRequest = this.saveNote(this.noteInput.value);
    await this.noteSaveRequest;
  }

  async saveNote(note) {
    this.setLoading(true);
    try {
      await FLC.fetchJSON(window.flcForge.routes.cart_update_url, {
        method: 'POST',
        body: JSON.stringify({ note })
      });
    } catch (err) {
      console.error('Cart note update failed:', err);
    } finally {
      this.setLoading(false);
      this.noteSaveRequest = null;
    }
  }

  /* ---- Fetch helpers ---- */

  // Full refresh: cart meta + rendered items in parallel
  async refreshCart() {
    this.setLoading(true);
    try {
      const [cart, sections] = await Promise.all([
        FLC.fetchJSON(`${window.flcForge.routes.cart_url}.js`),
        FLC.fetchJSON(`/?sections=${CART_SECTION}`)
      ]);
      this.renderCart(cart, sections[CART_SECTION]);
    } catch (err) {
      console.error('Cart refresh failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  // Fetch only the rendered items HTML (fallback when sections weren't in the cart response)
  async fetchItemsHTML() {
    const data = await FLC.fetchJSON(`/?sections=${CART_SECTION}`);
    return data[CART_SECTION] ?? '';
  }

  /* ---- Render ---- */

  async renderCart(cart, itemsHTML) {
    const isEmpty = cart.item_count === 0;

    this.updateCount(cart.item_count);
    this.subtotal.forEach(el => { el.textContent = FLC.formatMoneyNoCents(cart.total_price); });
    if (this.noteInput && document.activeElement !== this.noteInput) {
      this.noteInput.value = cart.note || '';
    }

    // Use pre-rendered HTML if provided; otherwise fetch it
    if (this.itemsWrap) {
      this.itemsWrap.innerHTML = itemsHTML ?? await this.fetchItemsHTML();
    }

    if (this.itemsWrap)  this.itemsWrap.hidden  = isEmpty;
    if (this.emptyState) this.emptyState.hidden = !isEmpty;
    if (this.footer)     this.footer.hidden     = isEmpty;

    this.updateThreshold(cart.total_price, isEmpty);
    this.updateDiscountThreshold(cart, isEmpty);
    this.updateDiscountSummary(cart);

    // DEV-80: refresh recommendation-based upsells whenever the cart changes.
    // Fire-and-forget — upsells must never block cart rendering.
    this.refreshUpsells(cart);

    document.body.dispatchEvent(new CustomEvent('initTheWishlist', { detail: { source: 'cart-drawer' } }));
  }

  /* ---- Upsells — Shopify product recommendations (DEV-80) ----
     Calls the Product Recommendations API, which server-renders
     sections/cart-upsell-products.liquid: AI recommendations for the most
     recently added cart item, minus products already in the cart, falling
     back to the curated collection (Theme settings -> Cart) when the engine
     returns nothing. The returned slides are injected into the shell
     rendered by snippets/cart-drawer.liquid. */

  async refreshUpsells(cart) {
    const wrap = this.upsellWrap;
    if (!wrap) return; // upsells disabled in theme settings

    // cart.js lists line items most-recently-added first — use it as the seed
    const seed = cart.items?.[0]?.product_id;
    if (!seed) {
      wrap.hidden = true;
      this.upsellKey = null;
      return;
    }

    // Skip the network round-trip when neither seed nor cart contents changed
    const key = `${seed}:${cart.items.map((i) => i.product_id).join(',')}`;
    if (key === this.upsellKey) return;

    const url =
      `${wrap.dataset.url}?section_id=${wrap.dataset.sectionId}` +
      `&product_id=${seed}&limit=${wrap.dataset.limit}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      const slides = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelectorAll('.cart-upsell__slide');

      const track = wrap.querySelector('[data-cart-upsell-track]');
      if (!track) return;
      track.replaceChildren(...slides);
      wrap.hidden = slides.length === 0;
      this.upsellKey = key;
      // Recalculate arrow disabled states for the new slide set
      wrap._slider?.updateButtons();
    } catch (err) {
      // Silently degrade: hide the upsell block, never break the cart
      console.error('Cart upsell fetch failed:', err);
      wrap.hidden = true;
      this.upsellKey = null;
    }
  }

  updateThreshold(totalCents, isEmpty) {
    if (!this.thresholdWrap) return;
    if (this.thresholdWrap.dataset.enabled !== 'true') return;

    const threshold   = parseInt(this.thresholdWrap.dataset.threshold, 10);
    if (!threshold)   return;

    const fill        = this.thresholdWrap.querySelector('[data-threshold-fill]');
    const message     = this.thresholdWrap.querySelector('[data-threshold-message]');
    const msgProgress = this.thresholdWrap.dataset.messageProgress;
    const msgReached  = this.thresholdWrap.dataset.messageReached;

    const pct       = isEmpty ? 0 : Math.min(100, Math.round((totalCents / threshold) * 100));
    const remaining = isEmpty ? threshold : Math.max(0, threshold - totalCents);

    if (fill)    fill.style.width = `${pct}%`;
    if (message) {
      if (remaining === 0) {
        message.textContent = msgReached;
        this.thresholdWrap.classList.add('is-reached');
      } else {
        message.textContent = msgProgress.replace('[[amount]]', FLC.formatMoney(remaining));
        this.thresholdWrap.classList.remove('is-reached');
      }
    }
  }

  updateDiscountThreshold(cart, isEmpty) {
    const wrap = this.discountThresholdWrap;
    if (!wrap) return;

    const threshold      = parseInt(wrap.dataset.threshold, 10);
    if (!threshold)      return;

    const fill           = wrap.querySelector('[data-threshold-fill]');
    const message        = wrap.querySelector('[data-threshold-message]');
    const discountLabel  = wrap.dataset.discountLabel || '';
    const msgProgress    = wrap.dataset.messageProgress;
    const msgReached     = wrap.dataset.messageReached;

    // Only count line items eligible for the matching Shopify discount (see
    // data-discount-eligible in sections/cart-line-items.liquid) — a plain
    // cart-wide total would show "reached" even when the discount targets
    // specific products the customer hasn't actually added enough of.
    // Use original_line_price (pre-discount) since Shopify evaluates the
    // discount's minimum-purchase requirement against the pre-discount
    // total — using the post-discount price would understate progress once
    // the discount is already applied.
    const eligibleCents = isEmpty ? 0 : (cart.items || []).reduce((sum, item) => {
      const itemEl = this.itemsWrap?.querySelector(`[data-key="${item.key}"]`);
      const eligible = itemEl ? itemEl.dataset.discountEligible !== 'false' : true;
      return eligible ? sum + item.original_line_price : sum;
    }, 0);

    const pct       = isEmpty ? 0 : Math.min(100, Math.round((eligibleCents / threshold) * 100));
    const remaining = isEmpty ? threshold : Math.max(0, threshold - eligibleCents);

    if (fill)    fill.style.width = `${pct}%`;
    if (message) {
      if (remaining === 0) {
        message.textContent = msgReached.replace('[[discount]]', discountLabel);
        wrap.classList.add('is-reached');
      } else {
        message.textContent = msgProgress
          .replace('[[amount]]', FLC.formatMoney(remaining))
          .replace('[[discount]]', discountLabel);
        wrap.classList.remove('is-reached');
      }
    }
  }

  updateDiscountSummary(cart) {
    const wrap = this.discountSummary;
    if (!wrap) return;

    const items = cart.items || [];
    let compareSavings = 0;
    items.forEach(item => {
      const itemEl = this.itemsWrap?.querySelector(`[data-key="${item.key}"]`);
      const compareAtPrice = parseInt(itemEl?.dataset.compareAtPrice, 10) || 0;
      if (compareAtPrice > 0) {
        const lineSavings = (compareAtPrice * item.quantity) - item.original_line_price;
        if (lineSavings > 0) compareSavings += lineSavings;
      }
    });

    const discountTotal = (cart.total_discount || 0) + compareSavings;
    const cartLevelTitle = (cart.cart_level_discount_applications || [])[0]?.title;
    const lineLevelTitle = items
      .flatMap(item => item.line_level_discount_allocations || [])
      .find(allocation => allocation.discount_application?.title)?.discount_application?.title;
    const discountTitle = cartLevelTitle || lineLevelTitle || '';
    const template       = wrap.dataset.messageTemplate || '';

    wrap.hidden = discountTotal <= 0;
    this.subtotalCompare.forEach(el => { el.hidden = discountTotal <= 0; });

    if (discountTotal <= 0) return;

    const amountEl  = wrap.querySelector('[data-cart-discount-amount]');
    const messageEl = wrap.querySelector('[data-cart-saved-message]');

    if (amountEl)  amountEl.textContent = `-${FLC.formatMoneyNoCents(discountTotal)}`;
    if (messageEl) {
      messageEl.textContent = template
        .replace('[[amount]]', FLC.formatMoneyNoCents(discountTotal))
        .replace('[[discount]]', discountTitle);
    }

    this.subtotalCompare.forEach(el => {
      el.textContent = FLC.formatMoneyNoCents(cart.total_price + discountTotal);
    });
  }

  updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ---- Cart mutations ---- */

  async updateQuantity(input) {
    const key = input.dataset.key;
    const qty = parseInt(input.value, 10);
    this.setLoading(true);
    try {
      const data = await FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty, sections: CART_SECTION })
      });
      this.renderCart(data, data.sections?.[CART_SECTION]);
    } catch (err) {
      console.error('Update quantity failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  // DEV-78: accepts the remove button element. When the line has attached
  // add-on services (data-addon-keys, see sections/cart-line-items.liquid),
  // the parent and all its add-ons are removed together via /cart/update.js.
  async removeItem(btn) {
    this.setLoading(true);
    try {
      const addonKeys = (btn.dataset.addonKeys || '').split(',').filter(Boolean);
      let data;

      if (addonKeys.length) {
        const updates = {};
        const parentKey = btn.closest('.cart-item')?.dataset.key;
        if (parentKey) updates[parentKey] = 0;
        addonKeys.forEach((k) => { updates[k] = 0; });

        data = await FLC.fetchJSON(window.flcForge.routes.cart_update_url, {
          method: 'POST',
          body: JSON.stringify({ updates, sections: CART_SECTION })
        });
      } else {
        data = await FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
          method: 'POST',
          body: JSON.stringify({ line: btn.dataset.lineIndex, quantity: 0, sections: CART_SECTION })
        });
      }

      this.renderCart(data, data.sections?.[CART_SECTION]);
      window.liveRegion?.announce('Item removed from cart');
    } catch (err) {
      console.error('Remove item failed:', err);
    } finally {
      this.setLoading(false);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ---------------------------------------------------------------------------
   Cart Upsell Slider
   A lightweight scroll-snap slider for the "You May Also Like" section.
--------------------------------------------------------------------------- */
class CartUpsellSlider {
  constructor(root) {
    this.track   = root.querySelector('[data-cart-upsell-track]');
    this.prevBtn = root.querySelector('[data-cart-upsell-prev]');
    this.nextBtn = root.querySelector('[data-cart-upsell-next]');
    if (!this.track) return;

    this.prevBtn?.addEventListener('click', () => this.scroll(-1));
    this.nextBtn?.addEventListener('click', () => this.scroll(1));
    this.track.addEventListener('scroll', () => this.updateButtons(), { passive: true });
    this.updateButtons();
  }

  get slideWidth() {
    return this.track.firstElementChild?.offsetWidth || 0;
  }

  scroll(dir) {
    this.track.scrollBy({ left: dir * (this.slideWidth + 12), behavior: 'smooth' });
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.prevBtn.disabled = scrollLeft <= 2;
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const upsell = document.querySelector('.cart-upsell');
  // DEV-80: keep a handle on the instance so CartDrawer.refreshUpsells()
  // can recalc arrow states after injecting fresh slides.
  if (upsell) upsell._slider = new CartUpsellSlider(upsell);
});

/* Cart quantity stepper buttons (outside the drawer, e.g. product page)
   ========================================================================== */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quantity-change]');
  if (!btn) return;

  const change = parseInt(btn.dataset.quantityChange, 10);
  const key    = btn.dataset.key;
  if (!key) return;

  const input = btn.parentElement?.querySelector('[data-quantity-input]');
  if (!input) return;

  const newQty = Math.max(0, parseInt(input.value, 10) + change);
  input.value = newQty;

  try {
    const data = await FLC.fetchJSON(window.flcForge.routes.cart_change_url, {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity: newQty, sections: CART_SECTION })
    });
    FLC.dispatch('flc:cart:updated', data);
  } catch (err) {
    console.error('Cart change failed:', err);
  }
});
